import {
  Component,
  DestroyRef,
  ElementRef,
  ResourceRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, debounceTime } from "rxjs";
import * as d3 from "d3";
import { ShotOverlayDto } from "../../../models/shotOverlayDto";
import { SharedModule } from "../../../shared/shared.module";
import { ShotLocations } from "../xwai-viz.types";
import { MergedShot, shotFitColorScale } from "../shot-overlay.utils";

/**
 * A self-contained slice viewer for XWAI visualizations. This is a slimmed-down
 * sibling of {@link MyViewerComponent}: it reuses the same D3 rendering core
 * (image + axes + zoom/pan + hover readout + colorbar legend) but takes every
 * value as an input, so it has no dependency on the model-viewer route stores
 * (`JobStateService` / `ViewerSettingsService`). That keeps it usable inside the
 * agentic XWAI panel where no job is loaded into those stores.
 *
 * The overlay features of the full viewer (sonic logs, horizons, shot overlay,
 * well markers) are intentionally omitted — XWAI only ever renders a plain slice.
 */
@Component({
  selector: "app-xwai-slice-viewer",
  imports: [SharedModule],
  templateUrl: "./xwai-slice-viewer.component.html",
  styleUrl: "./xwai-slice-viewer.component.less",
})
export class XwaiSliceViewerComponent {
  private destroyRef = inject(DestroyRef);

  public clipId = `xwai-chart-clip-${Math.random().toString(36).substring(2, 9)}`;
  public mouseCoords = signal<{ x: number; y: number } | null>(null);

  // Inputs — everything the viewer needs, supplied explicitly.
  sliceImage = input.required<string>();
  sliceArray = input.required<number[][]>();
  /** Colorbar image URL (drawn as the legend background). */
  colorbar = input<string>("");
  limits = input.required<{ min: number; max: number }>();
  /** Grid spacing, used to convert sample indices into physical distance. */
  dx = input.required<number>();
  xLabel = input<string>("X");
  yLabel = input<string>("Y");
  /** Shown as the legend caption (e.g. the model type). */
  legendLabel = input<string>("");
  viewerHeight = input<number>(360);
  flippedYAxis = input<boolean>(true);
  axisUnits = input<"km" | "kft">("km");
  smoothing = input<boolean>(true);
  // shots overlay for cases when its a depth slice
  protected shotsOverlayResource = input<ResourceRef<ShotOverlayDto[]>>()
  // shot source locations (keyed by shot number), merged with the overlay fits
  protected shotsLocResource = input<ResourceRef<ShotLocations>>()
  // editable colour-mapping range for the shots (by traces_fit)
  shotColorMin = input<number>(0);
  shotColorMax = input<number>(100);

  /** The currently hovered shot (drives the HTML tooltip); null when none. */
  protected hoveredShot = signal<
    (ShotOverlayDto & { sx: number; sy: number; sz: number }) | null
  >(null);
  /** Tooltip position in pixels, relative to the canvas container. */
  protected tooltipPos = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  private container =
    viewChild.required<ElementRef<HTMLDivElement>>("container");
  private resizeObserver!: ResizeObserver;
  private containerWidth$ = new BehaviorSubject<number>(0);
  private containerWidth = toSignal(
    this.containerWidth$.pipe(takeUntilDestroyed(), debounceTime(500)),
    { initialValue: 0 },
  );

  // Margins to make room for axes
  protected margin = { top: 5, right: 10, bottom: 33, left: 50 };

  // D3 selections
  private svgSelection!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private metricsGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private chartGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private mainImage!: d3.Selection<SVGImageElement, unknown, null, undefined>;
  private shotsGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Last merged fits+locations, kept so resizes can reposition without a refetch. */
  private mergedShots: MergedShot[] = [];
  private xAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private yAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;

  private xScale = d3.scaleLinear();
  private yScale = d3.scaleLinear();
  private zoomBehavior = d3.zoom<SVGSVGElement, unknown>();

  // Hover readout values, displayed in the metrics table.
  xText!: string;
  yText!: string;
  valueText: any;

  constructor() {
    effect(() => this.updateDimensions());
    effect(() => this.updateImage());
    effect(() => this.onMouseCordsChanged());
    effect(() => this.axisUnitsChanged());
    effect(() => {
      const overlay = this.shotsOverlayResource();
      const locations = this.shotsLocResource();
      // hasValue() narrows away the resource error state; thanks to defaultValue
      // value() is a resolved ShotOverlayDto[] / ShotLocations (empty or populated).
      if (!overlay?.hasValue() || !locations?.hasValue()) return;
      this.makeShotOverlay(overlay.value(), locations.value());
    })
    // Recolour shots when the editable colour range changes (no refetch needed).
    effect(() => {
      this.shotFitColor.domain([this.shotColorMin(), this.shotColorMax()]);
      this.recolorShots();
    })
  }

  ngOnInit() {
    this.initializeGraph();
    this.destroyRef.onDestroy(() => {
      this.svgSelection?.selectAll("*").remove();
    });
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.containerWidth$.next(entries[0].contentRect.width);
    });
    this.resizeObserver.observe(this.container().nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  /** Builds the SVG, scales, image and zoom behaviour. Runs once. */
  private initializeGraph() {
    let width =
      this.container().nativeElement.clientWidth -
      (this.margin.left + this.margin.right);
    const height = this.viewerHeight();
    if (width < 0) return;

    this.svgSelection = d3
      .select(this.container().nativeElement)
      .append("svg")
      .attr("width", width + this.margin.left + this.margin.right)
      .attr("height", height + this.margin.top + this.margin.bottom);

    // group for the co-ordinate + value readout
    this.metricsGroup = this.svgSelection
      .append("g")
      .attr(
        "transform",
        `translate(${this.margin.left}, ${height + this.margin.top + this.margin.bottom})`,
      );

    // clip path so the image doesn't spill over the axes while zooming
    const defs = this.svgSelection.append("defs");
    defs
      .append("clipPath")
      .attr("id", this.clipId)
      .append("rect")
      .attr("height", height)
      .attr("width", width)
      .attr("x", 0)
      .attr("y", 0);

    this.chartGroup = this.svgSelection
      .append("g")
      .attr("width", width)
      .attr("height", height)
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    this.xAxisGroup = this.chartGroup.append("g").attr("class", "x-axis-group");
    this.yAxisGroup = this.chartGroup.append("g").attr("class", "y-axis-group");

    this.yAxisGroup
      .append("text")
      .attr("class", "y-label")
      .attr("transform", `translate(-35, ${height / 2}) rotate(-90)`)
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text(`${this.yLabel()} (${this.axisUnits()})`);
    this.xAxisGroup
      .append("text")
      .attr("class", "x-label")
      .attr("transform", `translate(${width / 2}, 30)`)
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text(`${this.xLabel()} (${this.axisUnits()})`);

    const xDomain = this.sliceArray().length;
    const yDomain = this.sliceArray()[0].length;
    this.xScale = d3.scaleLinear().domain([0, xDomain]).range([0, width]);
    if (this.flippedYAxis()) {
      this.yScale = d3.scaleLinear().domain([0, yDomain]).range([0, height]);
    } else {
      this.yScale = d3.scaleLinear().domain([yDomain, 0]).range([0, height]);
    }
    this.renderAxes(this.xScale, this.yScale);

    const clipGroup = this.chartGroup
      .append("g")
      .attr("clip-path", `url(#${this.clipId})`);

    this.mainImage = clipGroup
      .append("image")
      .attr("width", width)
      .attr("height", height)
      .attr("class", "main-slice")
      .attr("preserveAspectRatio", "none");

    // Shot overlay circles live in the same clipped area as the image and get
    // the same zoom transform applied (see handleZoom), so they pan/zoom with it.
    this.shotsGroup = clipGroup.append("g").attr("class", "shot-overlay-group");

    // hover readout
    this.mainImage.on("mousemove", (event) => {
      const [mx, my] = d3.pointer(event);
      const transform = d3.zoomTransform(this.mainImage.node()!);
      this.mouseCoords.set({
        x: transform.invertX(mx),
        y: transform.invertY(my),
      });
    });

    // zoom & pan: pan always, wheel-zoom only with a modifier held
    this.zoomBehavior
      .scaleExtent([1, 20])
      .filter((event: any) => {
        if (event.type === "wheel") {
          return event.shiftKey || event.ctrlKey || event.metaKey;
        }
        return true;
      })
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .extent([
        [0, 0],
        [width, height],
      ])
      .on("zoom", (event) => this.handleZoom(event));

    this.mainImage.call(this.zoomBehavior as any);
  }

  /** Applies the image href and smoothing. Re-runs when either input changes. */
  private updateImage() {
    const image = this.sliceImage();
    const smoothing = this.smoothing();
    if (!this.mainImage) return;
    this.mainImage.attr("href", image);
    this.mainImage.attr("image-rendering", smoothing ? "auto" : "pixelated");
  }

  private handleZoom(event: d3.D3ZoomEvent<SVGSVGElement, any>) {
    const transform = event.transform;
    this.renderAxes(
      transform.rescaleX(this.xScale),
      transform.rescaleY(this.yScale),
    );
    this.mainImage.attr("transform", transform.toString());
    this.shotsGroup?.attr("transform", transform.toString());
  }

  public resetZoom() {
    this.mainImage
      ?.transition()
      .duration(300)
      .call(this.zoomBehavior.transform as any, d3.zoomIdentity);
  }

  private axisUnitsChanged() {
    const units = this.axisUnits();
    if (!this.svgSelection) return;
    this.renderAxes(this.xScale, this.yScale);
    this.yAxisGroup.select("text").text(`${this.yLabel()} (${units})`);
    this.xAxisGroup.select("text").text(`${this.xLabel()} (${units})`);
  }

  /** Picks a clean tick interval (in physical units) for the given domain. */
  private ticksResolution(
    values: number[],
    dx: number,
    units: "km" | "kft" = "km",
  ) {
    const startValue = Math.min(...values);
    const endValue = Math.max(...values);
    const unit_dx = dx * (units == "km" ? 1 : 3.28084);
    const applicableRange = (endValue - startValue) * unit_dx;

    const options = [100, 200, 500, 1000, 2000, 2500, 5000, 10000];
    let i = 0;
    while (i < options.length - 1 && applicableRange / options[i] >= 15) {
      i++;
    }
    const selectedInterval = options[i];
    const countTicks = Math.floor(applicableRange / selectedInterval);
    const dxInterval = selectedInterval / unit_dx;

    const ticks = [0];
    for (let j = 1; j <= countTicks; j++) {
      ticks.push(startValue + j * dxInterval);
    }
    return ticks;
  }

  private renderAxes(
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
  ) {
    const unit = this.axisUnits();
    const dx = this.dx();
    this.xAxisGroup
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(this.ticksResolution(xScale.domain(), dx, unit))
          .tickFormat((d) =>
            (Number(d) * dx * 0.001 * (unit == "km" ? 1 : 3.28084))
              .toFixed(1)
              .toString(),
          ),
      )
      .attr("transform", `translate(0,${this.viewerHeight()})`);
    this.yAxisGroup.call(
      d3
        .axisLeft(yScale)
        .tickValues(this.ticksResolution(yScale.domain(), dx, unit))
        .tickFormat((d) =>
          (Number(d) * dx * 0.001 * (unit == "km" ? 1 : 3.28084))
            .toFixed(1)
            .toString(),
        ),
    );
  }

  private onMouseCordsChanged() {
    const coords = this.mouseCoords();
    if (!coords) return;

    const dxIdx = Math.floor(this.xScale.invert(coords.x));
    const dyIdx = Math.floor(this.yScale.invert(coords.y));

    this.xText = (dxIdx * this.dx()).toFixed(0);
    this.yText = (dyIdx * this.dx()).toFixed(0);

    const data = this.sliceArray();
    const colIndex = dxIdx;
    const rowIndex = this.flippedYAxis() ? dyIdx : data.length - dyIdx;
    if (data[rowIndex] && data[rowIndex][colIndex] !== undefined) {
      this.valueText = data[rowIndex][colIndex].toFixed(2);
    } else {
      this.valueText = "N/A";
    }
  }

  /** Reacts to container resizes, keeping the SVG, scales and image in sync. */
  private updateDimensions() {
    const w = this.containerWidth() - (this.margin.left + this.margin.right);
    const h = this.viewerHeight();
    if (w <= 0 || h <= 0 || !this.svgSelection) return;

    const t = this.svgSelection
      .transition()
      .duration(500) as unknown as d3.Transition<d3.BaseType, any, any, any>;

    this.xAxisGroup
      .select(".x-label")
      .transition(t)
      .attr("transform", `translate(${w / 2}, 30)`);
    this.yAxisGroup
      .select(".y-label")
      .transition(t)
      .attr("transform", `translate(-35, ${h / 2}) rotate(-90)`);

    this.svgSelection
      .transition(t)
      .attr("width", w + this.margin.left + this.margin.right)
      .attr("height", h + this.margin.top + this.margin.bottom);

    this.svgSelection
      .transition(t)
      .select(`#${this.clipId} rect`)
      .attr("width", w)
      .attr("height", h);

    this.xScale.range([0, w]);
    this.yScale.range([0, h]);

    this.mainImage.transition(t).attr("width", w).attr("height", h);

    this.zoomBehavior
      .translateExtent([
        [0, 0],
        [w, h],
      ])
      .extent([
        [0, 0],
        [w, h],
      ]);

    untracked(() => this.renderAxes(this.xScale, this.yScale));
    this.positionShots();
  }


  /** Radius (px, at zoom = 1) of each shot marker. */
  private static readonly SHOT_RADIUS = 3;

  /** Colours shots by traces_fit: 0 → black, 100 → white (clamped). */
  private shotFitColor = shotFitColorScale(0, 100);

  /**
   * Draws the shot overlay on a depth slice: each fit is joined to its source
   * location (by `shot_id`) and rendered as a circle at the shot's (sx, sy),
   * converted from physical distance to sample index via `dx`. Called from the
   * effect whenever the fits or locations resources resolve.
   */
  private makeShotOverlay(data: ShotOverlayDto[], locations: ShotLocations) {
    if (!this.shotsGroup) return;

    // Pair each fit with its location; drop shots we have no location for.
    this.mergedShots = data
      .map((fit) => ({ ...fit, ...locations[fit.shot_id] }))
      .filter((shot) => shot.sx != null && shot.sy != null);

    // A redraw can drop the hovered shot; clear the tooltip to avoid a stale one.
    this.hoveredShot.set(null);

    this.shotsGroup
      .selectAll<SVGCircleElement, (typeof this.mergedShots)[number]>(
        ".shot-circle",
      )
      .data(this.mergedShots, (d) => d.shot_id)
      .join(
        (enter) =>
          enter
            .append("circle")
            .attr("class", "shot-circle")
            .attr("r", XwaiSliceViewerComponent.SHOT_RADIUS)
            .attr("fill", (d) => this.shotFitColor(d.traces_fit))
            .attr("stroke-width", 0)
            .style("cursor", "pointer")
            .on("mouseenter", (_event, d) => this.hoveredShot.set(d))
            .on("mousemove", (event) => {
              const [x, y] = d3.pointer(event, this.container().nativeElement);
              this.tooltipPos.set({ x, y });
            })
            .on("mouseleave", () => this.hoveredShot.set(null)),
        (update) => update.attr("fill", (d) => this.shotFitColor(d.traces_fit)),
        (exit) => exit.remove(),
      );

    this.positionShots();
  }

  /** Re-applies the fill of existing shots (e.g. after the colour range changes). */
  private recolorShots() {
    if (!this.shotsGroup) return;
    this.shotsGroup
      .selectAll<SVGCircleElement, (typeof this.mergedShots)[number]>(
        ".shot-circle",
      )
      .attr("fill", (d) => this.shotFitColor(d.traces_fit));
  }

  /** (Re)positions existing shot circles using the current scales. */
  private positionShots() {
    if (!this.shotsGroup) return;
    const dx = this.dx();
    this.shotsGroup
      .selectAll<SVGCircleElement, (typeof this.mergedShots)[number]>(
        ".shot-circle",
      )
      .attr("cx", (d) => this.xScale(d.sx / dx))
      .attr("cy", (d) => this.yScale(d.sy / dx));
  }
}
